"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createInventoryItemComparator = exports.compareInventoryItems = exports.inventorySortOrder = exports.buildInventorySortKeys = exports.inventoryTextSortKey = void 0;
/** Full key for local comparison. Persist only buildInventorySortKeys' prefix.
 * Fixed-width hexadecimal code points preserve lexical order using ASCII only.
 * A digit run occupies the '0' code point slot, followed by its significant
 * digit count and digits. Leading zeroes intentionally compare equal.
 */
function inventoryTextSortKey(value) {
    const text = typeof value === 'string'
        ? value.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase()
        : '';
    const tokens = text.match(/[0-9]+|[^0-9]/gu) || [];
    return tokens
        .map((token) => {
        if (/^[0-9]/.test(token)) {
            const digits = token.replace(/^0+(?=\d)/, '');
            return `000030${String(digits.length).padStart(10, '0')}${digits}/`;
        }
        return token.codePointAt(0).toString(16).padStart(6, '0');
    })
        .join('');
}
exports.inventoryTextSortKey = inventoryTextSortKey;
function timestampMillis(value) {
    var _a, _b, _c;
    // Inventory DTO dates are ISO Date strings, whose precision is milliseconds.
    // Use the same precision for SDK timestamps so database and UI ties agree.
    if (typeof value === 'number')
        return Number.isFinite(value) ? Math.trunc(value) : undefined;
    if (typeof value === 'string') {
        const millis = Date.parse(value);
        return Number.isFinite(millis) ? millis : undefined;
    }
    if (value instanceof Date)
        return timestampMillis(value.getTime());
    if (value && typeof value === 'object') {
        const timestamp = value;
        if (typeof timestamp.toMillis === 'function') {
            try {
                return timestampMillis(timestamp.toMillis());
            }
            catch {
                return undefined;
            }
        }
        const seconds = (_a = timestamp.seconds) !== null && _a !== void 0 ? _a : timestamp._seconds;
        const nanoseconds = (_c = (_b = timestamp.nanoseconds) !== null && _b !== void 0 ? _b : timestamp._nanoseconds) !== null && _c !== void 0 ? _c : 0;
        if (typeof seconds === 'number' && typeof nanoseconds === 'number') {
            return timestampMillis(seconds * 1000 + nanoseconds / 1000000);
        }
    }
    return undefined;
}
function numericKeys(data) {
    var _a, _b;
    const created = (_a = timestampMillis(data.createdAt)) !== null && _a !== void 0 ? _a : 0;
    return {
        created,
        edited: (_b = timestampMillis(data.lastEditedAt)) !== null && _b !== void 0 ? _b : created,
        quantity: typeof data.qty === 'number' && Number.isFinite(data.qty) ? data.qty : 0,
    };
}
/** ASCII prefixes stay below Firestore's indexed string limit. Prefixes are
 * monotone but can collide: readers MUST collect the entire equal-primary-key
 * bucket and sort by full keys before publishing it, including descending scans.
 */
function buildInventorySortKeys(data) {
    return {
        version: 1,
        keywords: inventoryTextSortKey(data.keywords).slice(0, 900),
        location: inventoryTextSortKey(data.location).slice(0, 900),
        ...numericKeys(data),
    };
}
exports.buildInventorySortKeys = buildInventorySortKeys;
function inventorySortOrder(sort) {
    const [field, direction] = sort.split('-');
    return { field: `inventorySort.${field}`, direction };
}
exports.inventorySortOrder = inventorySortOrder;
/** Unicode scalar ordering matches UTF-8 ordering, unlike JS UTF-16 comparison. */
function compareText(left, right) {
    let a = 0;
    let b = 0;
    while (a < left.length && b < right.length) {
        const leftPoint = left.codePointAt(a);
        const rightPoint = right.codePointAt(b);
        const difference = leftPoint - rightPoint;
        if (difference)
            return difference;
        a += leftPoint > 0xffff ? 2 : 1;
        b += rightPoint > 0xffff ? 2 : 1;
    }
    return left.length - a - (right.length - b);
}
function fullKeys(item) {
    return {
        ...numericKeys(item),
        keywords: inventoryTextSortKey(item.keywords),
        location: inventoryTextSortKey(item.location),
    };
}
function comparePreparedItems(left, right, sort, leftKeys, rightKeys) {
    var _a, _b;
    const { field, direction } = inventorySortOrder(sort);
    const key = field.slice('inventorySort.'.length);
    let primary;
    if (key === 'keywords' || key === 'location')
        primary = compareText(leftKeys[key], rightKeys[key]);
    else
        primary = leftKeys[key] - rightKeys[key];
    return ((direction === 'desc' ? -primary : primary) ||
        compareText(leftKeys.keywords, rightKeys.keywords) ||
        compareText(left.id, right.id) ||
        compareText((_a = left.collectionId) !== null && _a !== void 0 ? _a : '', (_b = right.collectionId) !== null && _b !== void 0 ? _b : ''));
}
function compareInventoryItems(left, right, sort) {
    return comparePreparedItems(left, right, sort, fullKeys(left), fullKeys(right));
}
exports.compareInventoryItems = compareInventoryItems;
/** Create once per sort/merge invocation, then discard. Items must not mutate
 * during that invocation. Long keyword strings are normalized once per object,
 * rather than once per comparison; no global cache retains inventory records.
 */
function createInventoryItemComparator(sort) {
    const cached = new WeakMap();
    const keys = (item) => {
        let value = cached.get(item);
        if (!value) {
            value = fullKeys(item);
            cached.set(item, value);
        }
        return value;
    };
    return (left, right) => comparePreparedItems(left, right, sort, keys(left), keys(right));
}
exports.createInventoryItemComparator = createInventoryItemComparator;
